import { useSuspenseQuery } from '@apollo/client';
import type { FC } from 'react';
import { Suspense, useEffect } from 'react';

import { Observer } from '@xing-com/crate-communication-in-view';
import { Redirect } from '@xing-com/crate-core-router';
import { discoNetworkUpdatesRoute } from '@xing-com/crate-discovery-routes';
import { useLoginState } from '@xing-com/crate-hooks-use-login-state';
import { useInsightsTracking } from '@xing-com/crate-insights-tracking';
import { GridContainer, GridItem } from '@xing-com/grid';

import {
  MetaData,
  SalaryBanner,
  StyledSection,
  TeaserSignUp,
} from './components';
import { HeroCarouselSection } from './components/hero-carousel-section';
import { JobsCarouselSection } from './components/jobs-carousel-section';
import { SalaryCarouselSection } from './components/salary-carousel-section';
import { GetInsightsSectionsDocument } from './graphql/get-insights-sections-query.gql-types';

const EntryInner: FC = () => {
  const { data } = useSuspenseQuery(GetInsightsSectionsDocument, {
    errorPolicy: 'all',
  });

  const heroArticles = data?.getInsightsHeroArticles?.articles?.filter(
    (article) => article !== null
  );
  const salaryArticles = data?.getInsightsSalaryArticles?.articles?.filter(
    (article) => article !== null
  );
  const jobsArticles = data?.getInsightsJobSearchArticles?.articles?.filter(
    (article) => article !== null
  );

  return (
    <>
      <StyledSection $hero>
        <HeroCarouselSection articles={heroArticles} />
      </StyledSection>

      <StyledSection>
        <SalaryCarouselSection articles={salaryArticles} />
      </StyledSection>

      <StyledSection>
        <SalaryBanner />
      </StyledSection>

      <StyledSection>
        <JobsCarouselSection articles={jobsArticles} />
      </StyledSection>

      <GridItem>
        <TeaserSignUp />
      </GridItem>
    </>
  );
};

export const Entry: FC = () => {
  const { isLoggedOut } = useLoginState();
  const { trackWithNWT } = useInsightsTracking();

  useEffect(() => {
    if (isLoggedOut) trackWithNWT({ event: 'viewed_screen' });
  }, [isLoggedOut, trackWithNWT]);

  if (!isLoggedOut) return <Redirect to={`/${discoNetworkUpdatesRoute}`} />;

  return (
    <>
      <MetaData />
      <Observer page="insights-logged-out">
        <GridContainer variant="expanded" data-qa="insights-entry-logged-out">
          <Suspense>
            <EntryInner />
          </Suspense>
        </GridContainer>
      </Observer>
    </>
  );
};
